import React, { useEffect, useState, useMemo } from "react";
import { Accordion, Row, Col, Form } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { mapUserRoles } from "../../../../functions/useRoleMapping";
import useHandleBlur from "../../../../functions/useHandleBlur";
import useTbdCheck from "../../../../functions/useTbdCheck";
import { useFormState } from "../../../../functions/useFormState";
import {
  selectUserDetails,
  selectUserRoles,
} from "../../../../redux/slices/userSlice";
import { selectApiUrls } from "../../../../redux/slices/apiUrlSlice";
import { selectWaterTreatment } from "../../../../redux/selectors";
import InletFilter from "./InletScreen";
import InletStrainer from "./InletStrainer";
import DischargeFilter from "./DischargeFilter";
import DischargeStrainer from "./DischargeStrainer";
import RO from "./RO";
import UV from "./UV";
import {
  setActiveFiltration,
} from "../../../../redux/slices/activeFiltrationSlice";
import {
  selectInletScreen,
  selectInletStrainer,
  selectDischargeStrainer,
  selectDischargeFilter,
  selectRo,
  selectUv,
} from "../../../../redux/selectors";
import { selectApiStatus } from "../../../../redux/slices/apiStatusSlice";
import NoRecordFoundAlert from "../../../common/NoRecordFoundAlert";

const Filtration = (zone) => {
  const dispatch = useDispatch();
  const userDetails = useSelector(selectUserDetails);
  const userRoles = useSelector(selectUserRoles);
  const currentUser = userDetails?.name;
  const sectionName = "waterTreatment";
  const jobId = useSelector((state) => state.jobId.jobId);
  const [roles, setRoles] = useState(mapUserRoles(userRoles));

  useEffect(() => {
    setRoles(mapUserRoles(userRoles));
  }, [userRoles]);

  const apiUrls = useSelector(selectApiUrls);
  const apiUrl = apiUrls[sectionName];
  const toggleApi = apiUrls["waterTreatment"];
  const tableKey = sectionName;

  const storedJobDetails = useSelector(selectWaterTreatment);

  const activetab = useSelector(
    (state) => state.activeMechanicalTab.selectedZone
  );

  const apiStatus = useSelector(selectApiStatus); // Fetch API status from Redux
  const loading = apiStatus[sectionName] !== "success"; // Determine loading state based on API status

  const error = null;

  const jobDetails = useMemo(() => {
    const detail = storedJobDetails.find((detail) => detail.Zone === activetab);
    return detail || storedJobDetails[0] || {};
  }, [storedJobDetails, activetab]);

  const { formData, handleChange, setFormData } = useFormState({});

  useEffect(() => {
    if (jobDetails) {
      setFormData({
        ID: jobDetails.ID || "",
        comments: jobDetails.Comments || "",
        filterComments: jobDetails.FilterComments || "",
      });
    }
  }, [jobDetails, setFormData]);

  const jobCheckoutStatus = useSelector((state) => state.checkout.job);
  const isJobCheckedOut = jobCheckoutStatus?.IsCheckedOut === true;
  const isCurrentUserCheckedOut =
    jobCheckoutStatus?.CheckedOutBy === currentUser;
  const readOnly = useSelector((state) => state.readOnly.isReadOnly); // Fetch read-only state from Redux
  const isDisabled = (isJobCheckedOut && !isCurrentUserCheckedOut) || readOnly; // Update isDisabled condition

  const inletFilterData = useSelector(selectInletScreen);
  const inletStrainerData = useSelector(selectInletStrainer);
  const dischargeFilterData = useSelector(selectDischargeFilter);
  const dischargeStrainerData = useSelector(selectDischargeStrainer);
  const roData = useSelector(selectRo);
  const uvData = useSelector(selectUv);

  const combinedFormData = useMemo(
    () => ({
      ...inletFilterData,
      ...inletStrainerData,
      ...dischargeFilterData,
      ...dischargeStrainerData,
      ...roData,
      ...uvData,
    }),
    [
      inletFilterData,
      inletStrainerData,
      dischargeFilterData,
      dischargeStrainerData,
      roData,
      uvData,
    ]
  );

  const { handleFocus, handleBlur, showNotification, saveStatus } =
    useHandleBlur(combinedFormData);

  useEffect(() => {
    setRoles(mapUserRoles(userRoles));
  }, [userRoles]);

  const { tbdFields } = useTbdCheck(combinedFormData, zone);
  const zoneTbdFields = zone ? tbdFields?.[zone] : tbdFields;

  const tbdList = useSelector((state) => state.tbdApi.tbdList);

  const [sectionTbd, setSectionTbd] = useState([]);

  useEffect(() => {
    // Reset sectionTbd to an empty array when the component loads
    setSectionTbd([]);
  }, []);

  useEffect(() => {
    // Update sectionTbd after fetching the tbdList
    const projectTbd = tbdList.filter(
      (tbdItem) => String(tbdItem.ProjectID) === String(jobId)
    );
    const uniqueTables = [
      ...new Set(projectTbd.map((tbdItem) => tbdItem.Table)),
    ];
    setSectionTbd(uniqueTables);
  }, [tbdList, jobId]);

  useEffect(() => {
    if (jobDetails) {
      const activeFiltration = {
        InletFilter: jobDetails.HasInletFilter,
        InletStrainer: jobDetails.HasInletStrainer,
        DischargeFilter: jobDetails.HasDischargeFilter,
        WyeStrainer: jobDetails.HasWyeStrainer,
        RO: jobDetails.HasRO,
        UV: jobDetails.HasUV,
      };
      dispatch(setActiveFiltration({ [jobDetails.ID]: activeFiltration }));
    }
  }, [dispatch, jobDetails]);

  const [accordionToggles, setAccordionToggles] = useState({
    InletFilter: "no",
    InletStrainer: "no",
    DischargeFilter: "no",
    WyeStrainer: "no",
    RO: "no",
    UV: "no",
  });

  useEffect(() => {
    if (jobDetails && jobDetails.Zone === activetab) {
      setAccordionToggles({
        InletFilter: jobDetails.HasInletFilter ? "yes" : "no",
        InletStrainer: jobDetails.HasInletStrainer ? "yes" : "no",
        DischargeFilter: jobDetails.HasDischargeFilter ? "yes" : "no",
        WyeStrainer: jobDetails.HasWyeStrainer ? "yes" : "no",
        RO: jobDetails.HasRO ? "yes" : "no",
        UV: jobDetails.HasUV ? "yes" : "no",
      });
    }
  }, [jobDetails, activetab]);

  const handleToggleChange = (section, value) => {
    setAccordionToggles((prevToggles) => ({
      ...prevToggles,
      [section]: value,
    }));
  };

  const hasTbdForSection = (section) => {
    return sectionTbd.some(
      (tbdItem) => {
        const normalizedTable = tbdItem.replace(/\s+/g, "").toLowerCase();
        const normalizedSection = section.replace(/\s+/g, "").toLowerCase();

        // Use exact matches for specific sections to avoid false positives
        switch (section) {
          case "Inlet Filter":
            return normalizedTable.includes("inletscreen");
          case "Inlet Strainer":
            return normalizedTable.includes("inletstrainer");
          case "Discharge Filter":
            return normalizedTable.includes("dischargefilter");
          case "Discharge Strainer":
            return normalizedTable.includes("dischargestrainer");
          case "RO":
            return normalizedTable.includes("rosystem");
          case "UV":
            return normalizedTable.includes("uvsystem");
          default:
            // For any other sections, use exact match to avoid substring conflicts
            return normalizedTable === normalizedSection;
        }
      }
    );
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    ); // Show loading spinner
  }

  if (error) {
    return <div className="error">{error}</div>; // Show error message if fetch fails
  }

  if (!loading && !jobDetails?.ID) {
    return <NoRecordFoundAlert sectionName={sectionName} zone={zone} />;
  }

  return (
    <div className="filtration-responsive">
      {error ? (
        <p style={{ color: "red" }}>Error: {error}</p>
      ) : !formData ? (
        <p>Loading...</p>
      ) : (
        <div>
          <div>
            {showNotification && (
              <div
                className={`save-notification ${
                  saveStatus.includes("Failed") ? "error" : ""
                }`}
              >
                {saveStatus}
              </div>
            )}
          </div>

          <Accordion alwaysOpen className="filtration-accordion">
            {[
              {
                name: "Inlet Filter",
                component: InletFilter,
                field: "HasInletFilter",
                key: "InletFilter",
              },
              {
                name: "Inlet Strainer",
                component: InletStrainer,
                field: "HasInletStrainer",
                key: "InletStrainer",
              },
              {
                name: "Discharge Filter",
                component: DischargeFilter,
                field: "HasDischargeFilter",
                key: "DischargeFilter",
              },
              {
                name: "Discharge Strainer",
                component: DischargeStrainer,
                field: "HasWyeStrainer",
                key: "WyeStrainer",
              },
              { name: "RO", component: RO, field: "HasRO", key: "RO" },
              { name: "UV", component: UV, field: "HasUV", key: "UV" },
            ].map((section, index) => {
              const SectionComponent = section.component;
              const toggleKey = section.key;
              const isDisabled = accordionToggles[toggleKey] === "no";
              const sectionId = jobDetails.ID;

              return (
                <Accordion.Item
                  eventKey={String(index)}
                  key={section.name}
                  className={isDisabled ? "disabled-accordion" : ""}
                >
                  <Accordion.Header
                    className={isDisabled ? "disabled-header" : ""}
                  >
                    <Row className="w-100 align-items-center filtration-accordion-header-row">
                      <Col xs="auto" className="d-flex align-items-center">
                        <Form.Check
                          disabled={!roles.Admin && !roles.Apps}
                          className="large-toggle"
                          type="switch"
                          id={`${section.name}-toggle`}
                          label=""
                          checked={accordionToggles[toggleKey] === "yes"}
                          onChange={(e) => {
                            const newValue = e.target.checked ? "yes" : "no";
                            handleToggleChange(toggleKey, newValue);

                            if (sectionId && accordionToggles[toggleKey] !== newValue) {
                              console.log("Toggle clicked:", {
                                sectionId,
                                toggleKey,
                                field: section.field,
                                newValue,
                                apiUrl: toggleApi
                              });
                              
                              handleBlur(
                                {
                                  target: {
                                    name: toggleKey,
                                    value: newValue === "yes",
                                    type: "switch"
                                  }
                                },
                                section.field, // Use section.field instead of toggleKey for the label
                                [],
                                {
                                  ID: sectionId,
                                  url: toggleApi,
                                  field: section.field,
                                  tableKey: sectionName // Use sectionName (waterTreatment) for consistency
                                }
                              );
                            }
                          }}
                          style={{ pointerEvents: "auto" }}
                        />
                      </Col>
                      <Col>
                        <h3>
                          {hasTbdForSection(section.name) && (
                            <i
                              className="bi bi-exclamation-diamond-fill"
                              style={{ color: "red" }}
                            />
                          )}{" "}
                          {section.name}
                        </h3>
                      </Col>
                    </Row>
                  </Accordion.Header>
                  {!isDisabled && (
                    <Accordion.Body>
                      {SectionComponent && <SectionComponent zone={zone} />}
                    </Accordion.Body>
                  )}
                </Accordion.Item>
              );
            })}
          </Accordion>
          <Row className="mb-3 mt-3 filtration-section-comments">
            <Col>
              <Form.Group controlId="filterComments">
                <Form.Label>Filter Comments</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="filterComments"
                  value={formData.filterComments || ""}
                  onFocus={handleFocus}
                  onBlur={(e) =>
                    handleBlur(
                      e,
                      "Filter Comments",
                      [],
                      {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "FilterComments",
                        tableKey,
                      }
                    )
                  }
                  disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                  className={zoneTbdFields?.filterComments ? "tbd-highlight" : ""}
                  onChange={handleChange}
                  placeholder="Enter Filter Comments"
                />
              </Form.Group>
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
};

export default Filtration;
